import React, { useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard, Package, Tag, Lightbulb, Mail, Users, FileText,
  Settings, Menu, X, LogOut, Home, Sliders, Info, Shield, Crown, Zap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import HOSLogo from '../components/HOSLogo';

const MASTER_NAV = [
  { to: '/admin',              icon: LayoutDashboard, label: 'Dashboard',     end: true },
  { to: '/admin/home-page',    icon: Home,            label: 'Home Page' },
  { to: '/admin/about-us',     icon: Info,            label: 'About Us' },
  { to: '/admin/site-settings',icon: Sliders,         label: 'Site Settings' },
  { to: '/admin/products',    icon: Package,         label: 'Products' },
  { to: '/admin/categories',  icon: Tag,             label: 'Categories' },
  { to: '/admin/solutions',   icon: Lightbulb,       label: 'Solutions' },
  { to: '/admin/content',      icon: Settings,        label: 'Content' },
  { to: '/admin/contacts',     icon: Mail,            label: 'Contacts' },
  { to: '/admin/auto-reply-mail', icon: Zap,          label: 'Auto Reply Mail' },
  { to: '/admin/users',        icon: Users,           label: 'Users',         masterOnly: true },
  { to: '/admin/submissions',  icon: FileText,        label: 'Submissions' },
];

// Manager sees only what master_admin has permitted
const MANAGER_NAV = [
  { to: '/admin',              icon: LayoutDashboard, label: 'Dashboard',     end: true },
  { to: '/admin/products',     icon: Package,         label: 'Products',      perm: ['products','view'] },
  { to: '/admin/categories',   icon: Tag,             label: 'Categories',    perm: ['products','view'] },
  { to: '/admin/solutions',    icon: Lightbulb,       label: 'Solutions',     perm: ['solutions','view'] },
  { to: '/admin/content',      icon: Settings,        label: 'Content',       perm: ['content','view'] },
  { to: '/admin/contacts',     icon: Mail,            label: 'Contacts',      perm: ['contacts','view'] },
  { to: '/admin/submissions',  icon: FileText,        label: 'My Submissions' },
  { to: '/admin/home-page',    icon: Home,            label: 'Home Page',     perm: ['homePage','view'] },
  { to: '/admin/about-us',     icon: Info,            label: 'About Us',      perm: ['aboutUs','view'] },
  { to: '/admin/site-settings',icon: Sliders,         label: 'Site Settings', perm: ['siteSettings','view'] },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isMasterAdmin, can } = useAuth();

  const navItems = isMasterAdmin
    ? MASTER_NAV
    : MANAGER_NAV.filter(item => !item.perm || can(item.perm[0], item.perm[1]));

  const roleLabel  = isMasterAdmin ? 'Master Admin' : (user?.role === 'manager' ? 'Web Manager' : user?.role);
  const roleColor  = isMasterAdmin ? '#B8A830' : '#4F8EF7';
  const RoleIcon   = isMasterAdmin ? Crown : Shield;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-100 shadow-sm transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:block flex flex-col`}>

        {/* Header */}
        <div className="p-5 border-b border-gray-100">
          <Link to="/"><HOSLogo size={32} /></Link>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: `${roleColor}20` }}>
              <RoleIcon size={11} style={{ color: roleColor }} />
            </div>
            <p className="text-xs font-semibold tracking-wider uppercase" style={{ color: roleColor }}>{roleLabel}</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all
                ${isActive
                  ? 'font-semibold'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`
              }
              style={({ isActive }) => isActive ? { background: `${roleColor}12`, color: roleColor } : {}}>
              {({ isActive }) => (
                <>
                  <Icon size={15} style={isActive ? { color: roleColor } : {}} />
                  {label}
                  {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: roleColor }} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm"
              style={{ background: `linear-gradient(135deg, ${roleColor}, ${roleColor}99)` }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{user?.name}</p>
              <p className="text-xs truncate" style={{ color: roleColor }}>{roleLabel}</p>
            </div>
          </div>
          {isMasterAdmin && (
            <NavLink to="/admin/profile"
              className={({ isActive }) =>
                `flex items-center gap-2 text-xs transition-colors w-full px-2 py-1.5 rounded-lg mb-1
                ${isActive ? 'bg-yellow-50 text-yellow-700 font-semibold' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50'}`}>
              <Users size={13} /> Personal Details
            </NavLink>
          )}
          <button onClick={logout}
            className="flex items-center gap-2 text-gray-400 hover:text-red-500 text-xs transition-colors w-full px-2 py-1.5 rounded-lg hover:bg-red-50">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between lg:hidden shadow-sm">
          <HOSLogo size={28} compact />
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-gray-700">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
